import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useCountry } from '../../hooks/useCountry';

const API_URL = import.meta.env.VITE_API_URL || '/api/v1';

const VerifyEmail = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const { theme, country: countryConfig, isSubdomain } = useCountry();
  const { i18n } = useTranslation();
  const colors = theme?.colors || {};

  const [loading, setLoading] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isSubdomain && countryConfig?.defaultLanguage && i18n.language !== countryConfig.defaultLanguage) {
      i18n.changeLanguage(countryConfig.defaultLanguage);
    }
  }, [isSubdomain, countryConfig, i18n]);

  useEffect(() => {
    const verifyEmail = async () => {
      if (!token) { setError('No verification token provided'); setLoading(false); return; }
      try {
        await axios.get(`${API_URL}/auth/verify-email/?token=${token}`);
        setSuccess(true);
      } catch (err) {
        setError(err.response?.data?.error || 'Failed to verify email. The link may have expired.');
      } finally {
        setLoading(false);
      }
    };
    verifyEmail();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col justify-center py-12 sm:px-6 lg:px-8" style={{ background: theme ? `linear-gradient(180deg, ${colors.primary}0A 0%, white 50%)` : '#f9fafb' }}>
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 mx-auto mb-4" style={{ borderColor: colors.primary || '#2563eb' }}></div>
            <h3 className="text-lg font-medium text-gray-900">Verifying your email...</h3>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col justify-center py-12 sm:px-6 lg:px-8" style={{ background: theme ? `linear-gradient(180deg, ${colors.primary}0A 0%, white 50%)` : '#f9fafb' }}>
      <div className="sm:mx-auto sm:w-full sm:max-w-md">
        <Link to="/" className="flex justify-center">
          <span className="text-3xl font-bold" style={{ color: colors.primary || '#2563eb' }}>RentyDoc</span>
        </Link>
      </div>

      <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
        <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10 text-center">
          {success ? (
            <>
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full mb-4" style={{ backgroundColor: `${colors.primary || '#16a34a'}15` }}>
                <svg className="h-6 w-6" style={{ color: colors.primary || '#16a34a' }} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Email Verified!</h3>
              <p className="text-gray-600 mb-6">Your email address has been verified. You can now access all features of RentyDoc.</p>
              <div className="space-y-3">
                <Link to="/dashboard" className="block w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white" style={{ background: theme ? colors.buttonBg : '#2563eb' }}>
                  Go to Dashboard
                </Link>
                <Link to="/" className="block w-full py-2 px-4 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50">
                  Create a Contract
                </Link>
              </div>
            </>
          ) : (
            <>
              <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 mb-4">
                <svg className="h-6 w-6 text-red-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Verification Failed</h3>
              <p className="text-gray-600 mb-6">{error}</p>
              <div className="space-y-3">
                <Link to="/login" className="block w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white" style={{ background: theme ? colors.buttonBg : '#2563eb' }}>
                  Go to Login
                </Link>
                <p className="text-sm text-gray-500">
                  Need a new verification link?{' '}
                  <Link to="/contact" className="hover:underline" style={{ color: colors.primary || '#2563eb' }}>Contact Support</Link>
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;
